import { Vec3 } from "@base/math/Vec3";
import { ImmHelper } from "@base/util/Immutable";
import { mapCount } from "@base/util/TypeUtil";
import { StandardTemplate } from "@biz/definitions/StandardTemplate";
import { IPatchDataRopes, ISunSailData, PoleId, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { StandardLogic } from "@biz/items/sunSail/logic/StandardLogic";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { createInitialPole, fillInitialPole } from "@biz/items/sunSail/SunSailInitial";
import { SunSailReducers } from "@biz/items/sunSail/SunSailReducers";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";

export interface ISetPoleDataOptions {
  fixPoleData?: boolean;
  fixPosition?: boolean;
  fixStandard?: boolean;
}

function updateStandardPoleCount(old: ISunSailData, newPoleCount: number): ISunSailData {
  const oldPositions = old.poles.map((p) => p.position);
  const center = Vec3.center(oldPositions);

  if (Poles.getCount(old) !== newPoleCount) {
    old = ImmHelper.setProperty(old, "poles", mapCount(newPoleCount, (i) => createInitialPole(old, newPoleCount, i, center, old.featureLevel)));
    old = ImmHelper.setProperty(old, "poles", mapCount(newPoleCount, (i) => fillInitialPole(old, i)));
  }

  return old;
}

export function updateStandardPositions(old: ISunSailData): ISunSailData {
  old = updateStandardPoleCount(old, StandardTemplate.getTemplatePoleCount(old.specific!.shape));
  const { polePositions } = StandardLogic.calculate(old);
  return SunSailReducers.setPolePositionsDirect(old, polePositions);
}

export function getSail2d3dReducers<T extends (ISunSailData | ISail2dForm)>(): {
  setPoleData: <ARGS extends any[]>(old: T, poleId: PoleId, options: ISetPoleDataOptions, fn: ((old: T["poles"][0], ...v: ARGS) => T["poles"][0]), ...val: ARGS) => T;
  setSandyOperatingRopePoleId(old: T, poleId: PoleId, operatingPoleId?: PoleId | undefined): T;
  setSandyPatchPoleId(old: T, patchIndex: number, pointIndex: number, poleId: PoleId): T;
  setPatchRopeArticleId(old: T, patchIndex: number, name: keyof IPatchDataRopes, articleId: string | undefined): T;
  setPatchRopeLength(old: T, patchIndex: number, name: keyof IPatchDataRopes, length: number | undefined): T;
} {
  function setPoleData<ARGS extends any[]>(old: T, poleId: PoleId, options: ISetPoleDataOptions, fn: ((old: T["poles"][0], ...v: ARGS) => T["poles"][0]), ...val: ARGS): T {
    if (!Poles.isValid(old, poleId))
      throw new Error("SunSailReducer.setPoleData: Invalid pole: " + poleId);
    const newPole = fn(Poles.getPole(old, poleId), ...val)
    const pi = Poles.fromPoleId(poleId)
    old = ImmHelper.setPropertyNonNullable(old, Poles.getPolePositionProperty(pi.polePositionType), pi.poleIndex, newPole);
    if ("itemType" in old) {
      // todo: remove
      let oldSSD = old as ISunSailData;
      if (options?.fixPoleData) {
        oldSSD = SunSailLogic.fixPoleData(oldSSD, poleId);
      }
      if (options?.fixPosition) {
        oldSSD = SunSailReducers.fixPosition(oldSSD, poleId);
      }
      if (options?.fixStandard) {
        if (old.sunSailType === SunSailType.Standard) {
          oldSSD = updateStandardPositions(oldSSD);
        }
      }
      old = oldSSD as T;
    }
    return old;
  }
  return {
    setPoleData,
    setSandyOperatingRopePoleId(old: T, poleId: PoleId, operatingPoleId?: PoleId | undefined): T {
      return setPoleData(old, poleId, {}, (oldPole, opi) => ImmHelper.setProperty(oldPole, "sandyOperatingRopePoleId", opi), operatingPoleId)
    },
    setSandyPatchPoleId(old: T, patchIndex: number, pointIndex: number, poleId: PoleId): T {
      const oldPatch = old.patches?.[patchIndex];
      if (!oldPatch)
        return old;

      const newPatch = ImmHelper.setArrayItem(oldPatch, "poleIds", pointIndex, poleId);
      return ImmHelper.setProperty(old, "patches", ImmHelper.setArrayItem(old.patches, patchIndex, newPatch))
    },
    setPatchRopeArticleId(old: T, patchIndex: number, name: keyof IPatchDataRopes, articleId: string | undefined): T {
      if (!SandyLogic.getPatch(old, patchIndex))
        return old;

      if (!old.patches?.[patchIndex]?.[name])
        old = ImmHelper.setPropertyNonNullable(old, "patches", patchIndex, name, {})

      return ImmHelper.setPropertyNonNullable(old, "patches", patchIndex, name, "articleId", articleId)
    },
    setPatchRopeLength(old: T, patchIndex: number, name: keyof IPatchDataRopes, length: number | undefined): T {
      if (!SandyLogic.getPatch(old, patchIndex))
        return old;

      if (!old.patches?.[patchIndex]?.[name])
        old = ImmHelper.setPropertyNonNullable(old, "patches", patchIndex, name, {})

      return ImmHelper.setPropertyNonNullable(old, "patches", patchIndex, name, "length", length)
    },
  }
}